import { isAnyOf } from '@reduxjs/toolkit';
import {
  newChatApprovedMessages_WS,
  newChatMessages_WS,
  newChatPendingMessages_WS,
  newChatRejectedMessages_WS,
} from 'pages/EventBroadcast/state/chat';
import { reactionReceived_WS } from 'pages/EventBroadcast/state/reactions';
import { selectIsChatV2EnabledInEvent } from 'store/apis/event';
import {
  audienceJoinedList_WS,
  audienceJoined_WS,
  bmmMeetingGuestHandDown_WS,
  bmmMeetingGuestHandUpList_WS,
  bmmMeetingGuestHandUp_WS,
  meetingGuestHandDown_WS,
  meetingGuestHandLoweredBy_WS,
  meetingGuestHandUp_WS,
  selectBroadcast,
} from 'store/features/event';

import type { Middleware } from '@reduxjs/toolkit';
import type { RootState } from 'store';

/**
 * When chat v2 is enabled AND the user is in a broadcast, chat messages, hands, audience, and room
 * reactions come exclusively from Ably. This middleware swallows the equivalent inbound
 * meeting-WebSocket messages so they never reach the reducers/listeners (single source of truth).
 * Placed FIRST in the middleware chain so swallowing prevents all downstream handling.
 *
 * IMPORTANT: it also gates on `selectBroadcast` (in-broadcast). The hand-raise messages are NOT
 * broadcast-specific — non-broadcast Big Meeting Mode nattering rooms use them to drive the
 * Backstage "requesting support" indicator. Chat v2 only touches the broadcast page, so a
 * community-wide flag alone would wrongly swallow those events for that community's regular events.
 */
const isGatedMeetingWsMessage = isAnyOf(
  newChatMessages_WS,
  newChatPendingMessages_WS,
  newChatApprovedMessages_WS,
  newChatRejectedMessages_WS,
  reactionReceived_WS,
  audienceJoined_WS,
  audienceJoinedList_WS,
  meetingGuestHandUp_WS,
  meetingGuestHandDown_WS,
  meetingGuestHandLoweredBy_WS,
  bmmMeetingGuestHandUp_WS,
  bmmMeetingGuestHandDown_WS,
  bmmMeetingGuestHandUpList_WS
);

export const chatV2MeetingWsGate: Middleware = (store) => (next) => (action) => {
  if (isGatedMeetingWsMessage(action)) {
    const state = store.getState() as RootState;
    if (selectIsChatV2EnabledInEvent(state) && selectBroadcast(state)) {
      return undefined;
    }
  }
  return next(action);
};
